import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import {
  FileText, DollarSign, AlertTriangle, CheckCircle, Info, Loader,
  BarChart3, TrendingUp, Shield, Calendar, Building2, Flame, Droplets,
  Wind, Factory, ChevronRight, Download, Crown, Sparkles, Clock,
  AlertCircle, FileCheck, Package, Lock
} from 'lucide-react';
import { PremiumGate } from '../components/PremiumGate';

// Data-quality badge: warns whenever a materiality verdict rests on a rule that
// is not in effect, so a green/red result is never read as a live obligation.
// Renders only from the backend `regulatory_status` field — the rule's legal
// status is a regulatory fact with a moving rescission timeline, so it is not
// duplicated client-side where it would silently go stale.
const RuleStatusBadge = ({ status }) => {
  if (!status || status.in_effect) return null;
  return (
    <span
      title={status.status}
      className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full border border-amber-200"
      data-testid="sec-rule-status-badge"
    >
      <AlertTriangle size={12} /> Illustrative — not a live SEC mandate
    </span>
  );
};

// Disclosure Requirements Component
const DisclosureRequirements = ({ requirements }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);

  const categoryIcons = {
    governance: Building2,
    strategy: TrendingUp,
    risk_management: Shield,
    metrics_targets: BarChart3,
    financial_statement: DollarSign
  };

  return (
    <div className="space-y-3">
      {Object.entries(requirements).map(([key, req]) => {
        const IconComponent = categoryIcons[key] || FileText;
        const isExpanded = expandedCategory === key;
        
        return (
          <div 
            key={key}
            className="bg-white rounded-xl border border-slate-200 overflow-hidden"
            data-testid={`disclosure-${key}`}
          >
            <button
              onClick={() => setExpandedCategory(isExpanded ? null : key)}
              className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <IconComponent size={20} className="text-blue-600" />
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-slate-900">{req.name}</h4>
                  <p className="text-xs text-slate-500">{req.items?.length || 0} disclosure items</p>
                </div>
              </div>
              <ChevronRight 
                size={20} 
                className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              />
            </button>
            
            {isExpanded && (
              <div className="px-5 pb-4 border-t border-slate-100">
                <ul className="space-y-2 mt-3">
                  {req.items?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Climate Impact Form Component
const ClimateImpactForm = ({ onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    event_type: 'physical_acute',
    event_description: '',
    financial_statement_line: 'operating_expenses',
    amount_usd: '',
    fiscal_year: new Date().getFullYear(),
    fiscal_quarter: null,
    is_capitalized: false,
    useful_life_years: null,
    geographic_location: '',
    total_revenue: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      amount_usd: parseFloat(formData.amount_usd) || 0,
      total_revenue: parseFloat(formData.total_revenue) || 0,
      useful_life_years: formData.is_capitalized ? parseInt(formData.useful_life_years) : null
    });
  };

  const eventTypes = [
    { value: 'physical_acute', label: 'Physical - Acute (storms, floods, fires)', icon: Flame },
    { value: 'physical_chronic', label: 'Physical - Chronic (sea level, drought)', icon: Droplets },
    { value: 'transition_policy', label: 'Transition - Policy (carbon tax)', icon: Building2 },
    { value: 'transition_technology', label: 'Transition - Technology (clean tech)', icon: Factory },
    { value: 'transition_market', label: 'Transition - Market (demand shifts)', icon: TrendingUp },
    { value: 'transition_reputation', label: 'Transition - Reputation (brand impact)', icon: Shield }
  ];

  const financialLines = [
    { value: 'revenue', label: 'Revenue' },
    { value: 'cost_of_goods_sold', label: 'Cost of Goods Sold' },
    { value: 'operating_expenses', label: 'Operating Expenses' },
    { value: 'capital_expenditures', label: 'Capital Expenditures' },
    { value: 'depreciation_amortization', label: 'Depreciation & Amortization' },
    { value: 'impairment_charges', label: 'Impairment Charges' },
    { value: 'contingent_liability', label: 'Contingent Liability' },
    { value: 'asset_retirement_obligation', label: 'Asset Retirement Obligation' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Event Type</label>
          <select 
            value={formData.event_type}
            onChange={(e) => handleChange('event_type', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {eventTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Financial Statement Line</label>
          <select 
            value={formData.financial_statement_line}
            onChange={(e) => handleChange('financial_statement_line', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {financialLines.map(line => (
              <option key={line.value} value={line.value}>{line.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Event Description</label>
        <textarea 
          value={formData.event_description}
          onChange={(e) => handleChange('event_description', e.target.value)}
          placeholder="Describe the climate event and its financial impact..."
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 h-20"
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Amount (USD)</label>
          <input 
            type="number"
            value={formData.amount_usd}
            onChange={(e) => handleChange('amount_usd', e.target.value)}
            placeholder="1000000"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Fiscal Year</label>
          <input 
            type="number"
            value={formData.fiscal_year}
            onChange={(e) => handleChange('fiscal_year', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Total Revenue (for materiality)</label>
          <input 
            type="number"
            value={formData.total_revenue}
            onChange={(e) => handleChange('total_revenue', e.target.value)}
            placeholder="100000000"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Geographic Location</label>
          <input 
            type="text"
            value={formData.geographic_location}
            onChange={(e) => handleChange('geographic_location', e.target.value)}
            placeholder="e.g., Texas, USA"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-4 pt-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input 
              type="checkbox"
              checked={formData.is_capitalized}
              onChange={(e) => handleChange('is_capitalized', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-sm text-slate-700">Capitalized Cost</span>
          </label>
          
          {formData.is_capitalized && (
            <div className="flex items-center gap-2">
              <input 
                type="number"
                value={formData.useful_life_years || ''}
                onChange={(e) => handleChange('useful_life_years', e.target.value)}
                placeholder="10"
                className="w-20 px-2 py-1 border border-slate-300 rounded text-sm"
              />
              <span className="text-sm text-slate-500">years useful life</span>
            </div>
          )}
        </div>
      </div>

      <button 
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="submit-climate-impact-btn"
      >
        {loading ? <Loader className="animate-spin" size={18} /> : <DollarSign size={18} />}
        {loading ? 'Recording...' : 'Record Climate Impact'}
      </button>
    </form>
  );
};

// Materiality Alert Component
const MaterialityAlert = ({ alert }) => {
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-50 border-red-200 text-red-800';
      case 'medium': return 'bg-amber-50 border-amber-200 text-amber-800';
      default: return 'bg-slate-50 border-slate-200 text-slate-800';
    }
  };

  return (
    <div className={`p-4 rounded-lg border ${getSeverityColor(alert.severity)}`}>
      <div className="flex items-start gap-3">
        <AlertCircle size={20} className={alert.severity === 'high' ? 'text-red-500' : 'text-amber-500'} />
        <div className="flex-1">
          <h5 className="font-bold mb-1">{alert.title}</h5>
          <p className="text-sm opacity-80">{alert.message}</p>
          <p className="text-xs mt-2 font-medium">{alert.action_required}</p>
        </div>
      </div>
    </div>
  );
};

// Main SEC Climate Notes Page
const SECClimateNotes = () => {
  const [requirements, setRequirements] = useState({});
  const [regulatoryStatus, setRegulatoryStatus] = useState(null);
  const [thresholdPct, setThresholdPct] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [impacts, setImpacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('record');
  const [submitResult, setSubmitResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const [reqData, alertsData, impactsData] = await Promise.all([
      apiFetch(`${API}/sec/disclosure-requirements`),
      apiFetch(`${API}/sec/materiality-alerts`),
      apiFetch(`${API}/sec/climate-impacts`)
    ]);

    if (reqData?._error || alertsData?._error || impactsData?._error) {
      console.error('Failed to fetch:', reqData?.message || alertsData?.message || impactsData?.message);
      setLoading(false);
      return;
    }

    setRequirements(reqData.requirements || {});
    // Rule status and 1% threshold are backend-authoritative — render them from
    // here rather than hardcoding the moving rescission timeline in page prose.
    setRegulatoryStatus(reqData.regulatory_status || null);
    setThresholdPct(reqData.materiality_threshold_pct ?? null);
    setAlerts(alertsData.alerts || []);
    setImpacts(impactsData.impacts || []);
    setLoading(false);
  };

  const handleSubmitImpact = async (formData) => {
    setSubmitting(true);
    const data = await apiFetch(`${API}/sec/record-climate-impact`, { method: 'POST', body: formData });
    if (data?._error) {
      console.error('Failed to submit:', data.message);
      setSubmitting(false);
      return;
    }
    setSubmitResult(data);
    // Refresh data
    fetchData();
    setSubmitting(false);
  };

  const handleGenerateNote = async () => {
    setSubmitting(true);
    const data = await apiFetch(`${API}/sec/generate-note`, {
      method: 'POST',
      body: {
        fiscal_year: new Date().getFullYear(),
        total_revenue: 100000000 // This should come from user input ideally
      }
    });
    if (data?._error) {
      console.error('Failed to generate:', data.message);
      setSubmitting(false);
      return;
    }
    setSubmitResult(data);
    setActiveTab('notes');
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sec-climate-notes-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h1 className="text-3xl font-bold">SEC Climate Notes Generator</h1>
              <span className="px-2 py-1 bg-amber-400 text-amber-900 text-xs font-bold rounded-full flex items-center gap-1">
                <Crown size={12} /> PREMIUM
              </span>
            </div>
            <p className="text-blue-200">
              Generate climate-related financial statement disclosures for SEC compliance
            </p>
          </div>
          <div className="flex gap-4">
            <div className="text-center bg-white/10 rounded-lg px-4 py-2">
              <p className="text-2xl font-bold">{impacts.length}</p>
              <p className="text-xs text-blue-200">Impacts Recorded</p>
            </div>
            <div className="text-center bg-white/10 rounded-lg px-4 py-2">
              <p className="text-2xl font-bold">{alerts.filter(a => !a.acknowledged).length}</p>
              <p className="text-xs text-blue-200">Active Alerts</p>
            </div>
          </div>
        </div>
      </div>

      <PremiumGate featureKey="sec-climate">
        {/* Materiality Alerts */}
        {alerts.filter(a => !a.acknowledged).length > 0 && (
          <div className="space-y-3">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-500" />
              Materiality Alerts
            </h3>
            {alerts.filter(a => !a.acknowledged).slice(0, 3).map((alert, idx) => (
              <MaterialityAlert key={idx} alert={alert} />
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200 pb-2">
          <button
            onClick={() => setActiveTab('record')}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
              activeTab === 'record' 
                ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-600' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="tab-record"
          >
            <DollarSign className="inline mr-2" size={16} />
            Record Impact
          </button>
          <button
            onClick={() => setActiveTab('requirements')}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
              activeTab === 'requirements' 
                ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-600' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="tab-requirements"
          >
            <FileText className="inline mr-2" size={16} />
            Requirements
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
              activeTab === 'history' 
                ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-600' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="tab-history"
          >
            <Clock className="inline mr-2" size={16} />
            History
          </button>
        </div>

        {/* Record Impact Tab */}
        {activeTab === 'record' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-bold text-slate-900 mb-4">Record Climate Financial Impact</h3>
                <ClimateImpactForm onSubmit={handleSubmitImpact} loading={submitting} />
              </div>

              {/* Submit Result */}
              {submitResult && submitResult.materiality_status && (
                <div className={`mt-4 p-4 rounded-xl border ${
                  submitResult.materiality_status === 'material' 
                    ? 'bg-red-50 border-red-200' 
                    : submitResult.materiality_status === 'approaching'
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-emerald-50 border-emerald-200'
                }`}>
                  <h4 className="font-bold mb-2 flex items-center gap-2">
                    {submitResult.materiality_status === 'material' ? (
                      <><AlertCircle className="text-red-500" size={18} /> Material Impact Recorded</>
                    ) : submitResult.materiality_status === 'approaching' ? (
                      <><AlertTriangle className="text-amber-500" size={18} /> Approaching Materiality</>
                    ) : (
                      <><CheckCircle className="text-emerald-500" size={18} /> Impact Recorded</>
                    )}
                  </h4>
                  <p className="text-sm">
                    This impact represents <strong>{submitResult.materiality_percentage?.toFixed(2)}%</strong> of total revenue.
                    {submitResult.materiality_status === 'material' && (
                      <> Exceeds the {thresholdPct != null ? <strong>{thresholdPct}%</strong> : 'materiality'} threshold — disclosure would be required if the SEC rule were in effect.</>
                    )}
                  </p>
                  <div className="mt-2">
                    <RuleStatusBadge status={submitResult.regulatory_status} />
                  </div>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h4 className="font-bold text-slate-900 mb-4">Quick Actions</h4>
                <div className="space-y-3">
                  <button 
                    onClick={handleGenerateNote}
                    disabled={submitting}
                    className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="generate-note-btn"
                  >
                    <FileCheck size={18} />
                    Generate Financial Note
                  </button>
                  <button className="w-full py-3 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors flex items-center justify-center gap-2">
                    <Package size={18} />
                    Create Attestation Package
                  </button>
                  <button className="w-full py-3 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors flex items-center justify-center gap-2">
                    <Download size={18} />
                    Export All Notes
                  </button>
                </div>
              </div>

              <div className="bg-amber-50 rounded-xl border border-amber-200 p-5">
                <h4 className="font-bold text-amber-900 mb-2">
                  {thresholdPct != null ? `${thresholdPct}% ` : ''}Materiality Threshold
                </h4>
                <p className="text-sm text-amber-800">
                  This platform applies the {thresholdPct != null ? <strong>{thresholdPct}%</strong> : 'materiality'} threshold
                  as <strong>illustrative preparer guidance</strong> for firms electing to disclose
                  climate-related financial impacts — not a live SEC mandate.
                </p>
                {regulatoryStatus && (
                  <p className="text-xs text-amber-700 mt-2">
                    {regulatoryStatus.rule} — {regulatoryStatus.status}.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Requirements Tab */}
        {activeTab === 'requirements' && (
          <div>
            <h3 className="font-bold text-slate-900 mb-4">SEC Climate Disclosure Requirements</h3>
            <DisclosureRequirements requirements={requirements} />
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            <h3 className="font-bold text-slate-900">Recorded Climate Impacts</h3>
            {impacts.length === 0 ? (
              <div className="text-center py-12 text-slate-500 bg-white rounded-xl border border-slate-200">
                <DollarSign size={48} className="mx-auto mb-4 opacity-50" />
                <p>No climate impacts recorded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {impacts.map((impact, idx) => (
                  <div key={idx} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`impact-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        impact.materiality_status === 'material' 
                          ? 'bg-red-100 text-red-700' 
                          : impact.materiality_status === 'approaching'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {impact.materiality_status?.toUpperCase() || 'RECORDED'}
                      </span>
                      <span className="text-sm text-slate-500">FY {impact.fiscal_year}</span>
                    </div>
                    <p className="font-medium text-slate-900 mb-1">{impact.event_description}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <span className="font-bold text-lg text-slate-900">${impact.amount_usd?.toLocaleString()}</span>
                      <span>{impact.financial_statement_line?.replace(/_/g, ' ')}</span>
                      <span>{impact.event_type?.replace(/_/g, ' ')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </PremiumGate>
    </div>
  );
};

export default SECClimateNotes;
